import api from "../api/api";
import { V2_BASE_URL } from "../api/bases";
import { getPracticeId, practiceDepartments } from "../config/practice";

/* User segregation by department (owner 11-03). The department lives in user_master.config
   (departmentid, all_departments); the server's location scoping already reads it. These calls are the
   NEW v2 routes (api/users/departments_admin.py), pinned to v2 like the corrections calls: they exist on
   v2 only and are for user administrators (403 for everyone else, which turns the feature off quietly). */
const V2 = { baseURL: V2_BASE_URL, _gw: "v2-pinned" };

export const fetchUsersWithDepartments = (params) =>
  api.get(`/v1/${getPracticeId()}/users/departments`, { ...V2, params });
export const setUserDepartment = (rowid, { departmentid, allDepartments, reason } = {}) =>
  api.put(
    `/v1/${getPracticeId()}/users/${rowid}/department`,
    { departmentid: Number(departmentid), all_departments: !!allDepartments, reason: reason || null },
    V2
  );
export const fetchUserDepartmentHistory = (rowid) =>
  api.get(`/v1/${getPracticeId()}/users/${rowid}/department/history`, V2);

// Filter values next to Role: "" = every user, a department id ("1"), ALL = all-departments users,
// NONE = users with no department yet.
export const DEPT_FILTER_ALL = "all";
export const DEPT_FILTER_NONE = "none";

/* The departments to offer: the server's registry when it answered, else the practice profile. */
export const departmentOptions = (serverDepartments) => {
  const list = Array.isArray(serverDepartments) && serverDepartments.length
    ? serverDepartments.map((d) => ({ id: String(d.departmentid), name: d.name }))
    : practiceDepartments().map((d) => ({ id: String(d.id), name: d.name }));
  return list.filter((d) => d.id && d.name);
};

/* {id -> department block} from the server's list rows. */
export const departmentsById = (rows) => {
  const out = {};
  (Array.isArray(rows) ? rows : []).forEach((r) => {
    if (r && r.id != null && r.department) out[String(r.id)] = { ...r.department, service_account: !!r.service_account };
  });
  return out;
};

export const userDepartment = (byId, user) => (user && byId ? byId[String(user.id)] || null : null);

export const departmentLabel = (dept) => {
  if (!dept) return "";
  return dept.label || dept.name || "";
};

export const matchesDepartmentFilter = (dept, filter) => {
  if (!filter) return true;
  if (!dept) return false;
  if (filter === DEPT_FILTER_ALL) return !!dept.all_departments;
  if (filter === DEPT_FILTER_NONE) return dept.state === "untagged";
  return String(dept.departmentid ?? "") === String(filter);
};

export const departmentFilterName = (filter, options) => {
  if (!filter) return "";
  if (filter === DEPT_FILTER_ALL) return "All departments";
  if (filter === DEPT_FILTER_NONE) return "Department not set";
  const hit = (options || []).find((o) => o.id === String(filter));
  return hit ? hit.name : `Department ${filter}`;
};

/* What the Edit User picker starts from: {departmentid: "1" | "", allDepartments: bool}. */
export const pickerState = (dept) => ({
  departmentid: dept && dept.departmentid != null ? String(dept.departmentid) : "",
  allDepartments: !!(dept && dept.all_departments),
});

export const pickerChanged = (initial, current) =>
  !!current.departmentid &&
  (String(initial.departmentid) !== String(current.departmentid) ||
    !!initial.allDepartments !== !!current.allDepartments);

export const errorText = (err, fallback = "The department was not saved.") => {
  const d = err?.response?.data;
  if (typeof d?.message === "string" && d.message) return d.message;
  if (typeof d?.detail === "string" && d.detail) return d.detail;
  return fallback;
};
